import { ActionIcon, Button, Card, Group, Stack, Text } from '@mantine/core';
import { ContactPoint, OrganizationContact } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { IconX, IconPlus, IconPhone } from '@tabler/icons-react';
import { useState } from 'react';
import { alert } from '@/services/alert';
import { inferSystem, getSystemIcon } from '../../services/organization';
import classes from './PartnerEditDrawer.module.css';
import { ORGANIZATION_CONTACT_PHOTO_EXTENSION, ORGANIZATION_CONTACT_TITLE_EXTENSION } from '@bariendo/lib/extensions';
import { ConditionalLayout } from '@/components/ConditionalLayout';
import { AvatarPreview } from './AvatarPreview';
import { EditableField, FaxEditableField, SmartEditableField } from '@/components/EditableField';
import { TelecomEntry } from './TelecomEntry';

function SystemIcon({ system }: { system?: string }) {
  const Icn = getSystemIcon(system ?? '', IconPhone);
  return <Icn size={16} color="gray" />;
}

export function CreatePartnerContactCard({
  onCreate,
  onCancel,
}: {
  onCreate: (contact: OrganizationContact) => void;
  onCancel: () => void;
}) {
  const medplum = useMedplum();
  const [form, setForm] = useState({ given: '', family: '', title: '' });
  const [telecom, setTelecom] = useState<ContactPoint[]>([]);
  const [avatar, setAvatar] = useState<File | null>(null);
  const [addingNew, setAddingNew] = useState(false);
  const [newValue, setNewValue] = useState('');
  const [expandingFax, setExpandingFax] = useState(false);
  const [newFaxValue, setNewFaxValue] = useState('');

  const validateForm = () => {
    if (!form.given) {
      alert.error('Please enter a given name');
      return false;
    }
    if (!form.family) {
      alert.error('Please enter a family name');
      return false;
    }
    if (!form.title) {
      alert.error('Please enter a title');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    const extensions = [];
    if (form.title) {
      extensions.push({
        url: ORGANIZATION_CONTACT_TITLE_EXTENSION,
        valueString: form.title,
      });
    }
    if (avatar) {
      extensions.push({
        url: ORGANIZATION_CONTACT_PHOTO_EXTENSION,
        valueAttachment: await medplum.createAttachment({
          contentType: 'image/jpeg',
          data: avatar,
        }),
      });
    }

    const telecomEntries = telecom
      .filter((t) => t.value?.trim())
      .map((t) => ({ system: t.system, value: t.value!.trim() }));

    const newContact: OrganizationContact = {
      name: { given: [form.given], family: form.family },
      extension: extensions.length > 0 ? extensions : undefined,
      telecom: telecomEntries,
    };

    onCreate(newContact);
    onCancel();
  };

  const resetFax = () => {
    setNewFaxValue('');
    setExpandingFax(false);
  };

  return (
    <Card withBorder radius="md" p="md" style={{ width: '100%' }}>
      <ActionIcon
        variant="subtle"
        color="gray"
        size="sm"
        onClick={onCancel}
        style={{ position: 'absolute', top: '10px', right: '10px' }}
      >
        <IconX size={16} />
      </ActionIcon>

      <Group justify="space-between" align="flex-start" maw="100%">
        <Stack gap="md" w="100%">
          <Group gap="md" w="90%">
            <AvatarPreview
              value={avatar}
              onChange={setAvatar}
              givenName={form.given}
              familyName={form.family}
              size={42}
            />

            <Stack gap={3} style={{ flex: 1, minWidth: 0 }}>
              <ConditionalLayout inlineCondition={!!(form.given && form.family)} gap={3}>
                <EditableField
                  value={form.given}
                  onSubmit={(value) => setForm({ ...form, given: value })}
                  editable
                  placeholder="Enter given name..."
                  inputProps={{ size: 'xs', w: form.given && form.family ? 'auto' : '100%' }}
                  textProps={{ size: 'sm', c: 'dimmed', style: { width: form.given && form.family ? 'auto' : '100%' } }}
                />
                <EditableField
                  value={form.family}
                  onSubmit={(value) => setForm({ ...form, family: value })}
                  editable
                  placeholder="Enter family name..."
                  inputProps={{ size: 'xs', w: form.given && form.family ? 'auto' : '100%' }}
                  textProps={{ size: 'sm', c: 'dimmed', style: { width: form.given && form.family ? 'auto' : '100%' } }}
                />
              </ConditionalLayout>

              <EditableField
                value={form.title}
                onSubmit={(value) => setForm({ ...form, title: value })}
                editable
                placeholder="Job title"
                inputProps={{ size: 'xs', w: '100%' }}
                textProps={{ size: 'sm', c: 'dimmed', w: '100%' }}
              />
            </Stack>
          </Group>

          {/* Contact methods */}
          <Stack gap={4} mt={4} w="100%">
            <Group justify="space-between" align="center" w="100%">
              <Text size="xs" c="gray">
                Contact methods
              </Text>
              <ActionIcon
                onClick={() => setAddingNew((v) => !v)}
                variant="subtle"
                color="gray"
                size={16}
                radius="md"
                aria-label="Add contact method"
              >
                <IconPlus size={16} />
              </ActionIcon>
            </Group>

            {telecom
              .filter((t) => t.system !== 'fax')
              .map((t, index) => {
                const actualIndex = telecom.findIndex((tel) => tel === t);
                return (
                  <TelecomEntry
                    key={t.id ?? `${t.system}-${index}`}
                    entry={t}
                    index={index}
                    actualIndex={actualIndex}
                    onUpdate={(actualIdx, val) => {
                      const updated = [...telecom];
                      updated[actualIdx] = { ...updated[actualIdx], value: val.trim() };
                      setTelecom(updated);
                    }}
                    onDelete={(actualIdx) => {
                      setTelecom(telecom.filter((_, i) => i !== actualIdx));
                    }}
                    options={{ dynamicSystem: true }}
                  />
                );
              })}

            {addingNew && (
              <Group gap="xs" wrap="nowrap" w="100%" className={classes.aboutRow}>
                <SystemIcon system={inferSystem(newValue)} />
                <SmartEditableField
                  value={newValue}
                  placeholder="Enter phone, email, or website"
                  inputProps={{ size: 'xs', style: { width: '100%' } }}
                  editable
                  focus={addingNew}
                  dynamicSystem={true}
                  onChange={setNewValue}
                  onSubmit={(v) => {
                    const trimmed = v.trim();
                    if (trimmed) {
                      const system = inferSystem(trimmed) ?? 'phone';
                      setTelecom([...telecom, { system: system as ContactPoint['system'], value: trimmed }]);
                    }
                    resetFax();
                  }}
                  onCancel={resetFax}
                  onEndEditing={(info) => {
                    if (!info.value) {
                      resetFax();
                    }
                  }}
                />
              </Group>
            )}
          </Stack>

          {/* Fax number */}
          <Stack gap={4} mt={4} w="100%">
            <Group justify="space-between" align="center" w="100%">
              <Text size="xs" c="gray">
                Fax number
              </Text>
              <ActionIcon
                onClick={() => setExpandingFax((v) => !v)}
                variant="subtle"
                color="gray"
                size={16}
                radius="md"
                aria-label="Add fax number"
              >
                <IconPlus size={16} />
              </ActionIcon>
            </Group>

            {telecom
              .filter((t) => t.system === 'fax')
              .map((fax, index) => {
                const actualIndex = telecom.findIndex((tel) => tel === fax);
                return (
                  <TelecomEntry
                    key={fax.id ?? `fax-${index}`}
                    entry={fax}
                    index={index}
                    actualIndex={actualIndex}
                    onUpdate={(actualIdx, val) => {
                      const updated = [...telecom];
                      updated[actualIdx] = { ...updated[actualIdx], value: val.trim() };
                      setTelecom(updated);
                    }}
                    onDelete={(actualIdx) => {
                      setTelecom(telecom.filter((_, i) => i !== actualIdx));
                    }}
                    options={{
                      dynamicSystem: false,
                      placeholder: 'Enter fax number',
                      ariaLabel: 'Delete fax number',
                    }}
                  />
                );
              })}

            {expandingFax && (
              <Group gap="xs" wrap="nowrap" w="100%" className={classes.aboutRow}>
                <SystemIcon system="fax" />
                <FaxEditableField
                  value={newFaxValue}
                  placeholder="+18136056242"
                  inputProps={{ size: 'xs', style: { width: '100%' } }}
                  editable
                  focus={expandingFax}
                  onChange={setNewFaxValue}
                  onSubmit={(v) => {
                    const trimmed = v.trim();
                    if (trimmed) {
                      setTelecom([...telecom, { system: 'fax', value: trimmed } as ContactPoint]);
                    }
                    resetFax();
                  }}
                  onCancel={resetFax}
                  onEndEditing={(info) => {
                    if (!info.value) {
                      resetFax();
                    }
                  }}
                />
              </Group>
            )}
          </Stack>
        </Stack>
      </Group>

      <Group justify="flex-end" mt="md">
        <Button size="xs" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button size="xs" onClick={handleSubmit}>
          Save Contact
        </Button>
      </Group>
    </Card>
  );
}
